import * as THREE from "three";
import type { LoadedHuman } from "./assetLoader";

export interface ExportedEuler {
  x: number;
  y: number;
  z: number;
}

export interface ExportedPerson {
  role: string;
  landmarks: Record<string, number>;
  bones: Record<string, ExportedEuler>;
}

export interface ExportedPose {
  persons: ExportedPerson[];
}

const tmp = new THREE.Vector3();
const euler = new THREE.Euler();

export function exportPose(humans: LoadedHuman[]): ExportedPose {
  return { persons: humans.map(exportHuman) };
}

export function applyPose(humans: LoadedHuman[], pose: ExportedPose): void {
  const people = pose.persons ?? [];
  for (let i = 0; i < humans.length && i < people.length; i++) {
    applyHuman(humans[i], people[i]);
  }
}

function exportHuman(human: LoadedHuman): ExportedPerson {
  const landmarks: Record<string, number> = {};
  const bones: Record<string, ExportedEuler> = {};
  const id = (human.def.id || "").toLowerCase();
  const role = id.includes("flyer") ? "flyer" : id.includes("base") ? "base" : "base";
  human.root.updateWorldMatrix(true, true);
  human.root.traverse((obj) => {
    if (!(obj instanceof THREE.Bone)) return;
    const key = landmarkKey(obj.name);
    if (key) {
      obj.getWorldPosition(tmp);
      landmarks[`${key}_x`] = round(tmp.x);
      landmarks[`${key}_y`] = round(tmp.y);
      landmarks[`${key}_z`] = round(tmp.z);
    }
    euler.setFromQuaternion(obj.quaternion, "XYZ");
    bones[obj.name] = { x: round(euler.x), y: round(euler.y), z: round(euler.z) };
  });
  return { role, landmarks, bones };
}

function applyHuman(human: LoadedHuman, person: ExportedPerson): void {
  const bones = person.bones ?? {};
  human.root.traverse((obj) => {
    if (!(obj instanceof THREE.Bone)) return;
    const e = bones[obj.name];
    if (!e) return;
    obj.quaternion.setFromEuler(new THREE.Euler(e.x, e.y, e.z, "XYZ"));
  });
  human.root.updateWorldMatrix(true, true);
}

function landmarkKey(name: string): string | null {
  const n = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (n.endsWith("hips") || n === "hip" || n === "pelvis" || n.endsWith("hip")) return "hip";
  if (n.includes("head") && !n.includes("headtop")) return "head";
  if (n.includes("leftfoot") || n.endsWith("lfoot")) return "lfoot";
  if (n.includes("rightfoot") || n.endsWith("rfoot")) return "rfoot";
  if (n.includes("lefthand") || n.endsWith("lhand")) return "lhand";
  if (n.includes("righthand") || n.endsWith("rhand")) return "rhand";
  return null;
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}
